/**
 * H7 greybox start towns B-D: one shared 96 x 96 m flat layout (round plaza in the middle, a warehouse and a
 * training hall on its north side, a monster pocket in the south-east corner, an exit gap in the north wall).
 * townB.ts / townC.ts / townD.ts only give each its id, name and look. TODO-polish: real layouts per
 * docs/design/C0_TOWNS_BCD.md.
 */
import type { LayoutSpec } from '../../layout';
import type { ZoneDef, ZoneId } from '../zones';

const S = 96;
/** Plaza: centre (tile) and radius (m); the safe circle in safeZones.ts uses the same numbers. */
export const TOWN_PLAZA = { at: [48, 52], r: 16 } as const;

export function townLayout(zone: string, seed: number): LayoutSpec {
  return {
    id: zone,
    zone: zone as ZoneId,
    w: S,
    h: S,
    seed,
    keepClear: 0,
    terrain: {
      coast: [
        [0, -10],
        [S, -10],
      ],
      west: [[Infinity, -10]],
      east: [1000, 0, 1, 0],
      south: [1000, 0, 1, 0, 0, 0],
      rock: { lift: 0, rise: 0, max: 0, noise: 0, scale: 1, step: 1 },
      rows: [
        [0, 0],
        [S, 0],
      ],
      slopes: [],
      plateaus: [],
      rises: [],
      hills: [],
      ridges: [],
      noise: [10, 0],
      beach: { reach: 0, base: 0, rise: 0, sand: 0, keep: [] },
    },
    ops: [
      { name: 'plaza', shape: { disc: [TOWN_PLAZA.at[0], TOWN_PLAZA.at[1], TOWN_PLAZA.r] }, kind: 'court', level: 0 },
      { name: 'road N', shape: { rect: [45, 52, 3, 36] }, kind: 'road', level: 0 },
      // Boundary wall; the exit gap is x 44-52 in the north.
      { name: 'wall N west', shape: { rect: [0, 44, 0, 3] }, kind: 'wall', level: 0 },
      { name: 'wall N east', shape: { rect: [52, S, 0, 3] }, kind: 'wall', level: 0 },
      { name: 'wall S', shape: { rect: [0, S, S - 3, S] }, kind: 'wall', level: 0 },
      { name: 'wall W', shape: { rect: [0, 3, 0, S] }, kind: 'wall', level: 0 },
      { name: 'wall E', shape: { rect: [S - 3, S, 0, S] }, kind: 'wall', level: 0 },
    ],
    buildings: [
      {
        id: `${zone}-warehouse`,
        name: 'โรงคลัง',
        kind: 'granary',
        x0: 30,
        x1: 40,
        y0: 38,
        y1: 46,
        base: 0,
        eave: 3.4,
        rise: 2.4,
        roof: 'hip',
        door: [35, 45, 's'],
      },
      {
        id: `${zone}-trainhall`,
        name: 'โรงฝึกยุทธ์',
        kind: 'hall',
        x0: 56,
        x1: 66,
        y0: 38,
        y1: 46,
        base: 0,
        eave: 3.6,
        rise: 2.6,
        roof: 'hip',
        door: [61, 45, 's'],
        color: 'red',
      },
    ],
    boats: [],
    posts: [],
    props: [],
    landmarks: {},
    points: {
      plaza: [48, 52],
      spawn: [48, 56],
      elder: [48, 48],
      jail: [48, 52],
      duel: [48, 60],
      arena: [48, 60],
      lair: [76, 78],
    },
    regions: {
      wolves: [66, 86, 68, 86],
      bandits: [66, 86, 68, 86],
      west: [8, 20, 60, 80],
      east: [76, 88, 30, 50],
      graves: [8, 20, 30, 50],
    },
    portals: [],
    reserved: [],
    // The plaza is the safe circle (octagon); safeZones.ts adds the true circle.
    safe: [
      [32, 52],
      [37, 40],
      [48, 36],
      [59, 40],
      [64, 52],
      [59, 64],
      [48, 68],
      [37, 64],
    ],
  };
}

/** A town's zone. `at` [0,1] is south of the plains, so the map gets an exit warp (north edge) back to it. */
export function townZone(id: string, name: string, seed: number, tint: readonly [number, number, number]): ZoneDef {
  return {
    id: id as ZoneId,
    at: [0, 1],
    name,
    kind: 'village',
    terrain: 'plains',
    size: S,
    seed,
    layout: id,
    offGrid: true,
    tint,
    music: 'yanyu',
    wildMusic: 'wilds',
    levels: 'ระดับ 2–6',
    spawns: [{ monster: 'wolf', region: 'wolves', n: 6 }],
  };
}
